import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { CheckCircle2 } from 'lucide-react';
import { toast } from '../../../../../../ui/toast';
import { createJDTemplate } from '../../../../../../utils/api_service/JobOpenings';
import {
  JobDescription,
  JobDescriptionTemplate,
} from '../../../../../../utils/types/JobOpenings';
import { SelectOptions } from '../../../../../../utils/types/react_select_types';

interface CreateTemplateProps {
  closeModal: () => void;
  jobDescription: JobDescription;
  onTemplateSave?: (templateData: JobDescriptionTemplate) => void;
  jobTitle: SelectOptions | undefined;
}

export const CreateTemplate: React.FC<CreateTemplateProps> = ({
  closeModal,
  jobDescription,
  onTemplateSave,
  jobTitle,
}) => {
  const today = new Date();
  const day = String(today.getDate()).padStart(2, '0');
  const month = today.toLocaleString('en-GB', { month: 'short' });
  const year = today.getFullYear();
  const formattedDate = `${day}/${month}/${year}`;

  const [templateName, setTemplateName] = useState(
    jobTitle?.label ? `${jobTitle.label} - ${formattedDate}` : '',
  );

  const [templateDescription, setTemplateDescription] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const handleAddTag = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const newTag = tagInput.trim();
      if (newTag && !tags.includes(newTag) && tags.length < 8) {
        setTags([...tags, newTag]);
        setTagInput('');
      }
    }
  };

  const removeTag = (tagToRemove: string) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  const handleSave = async () => {
    setIsSaving(true);

    const templateData: JobDescriptionTemplate = {
      templateTitle: templateName,
      templateDescription: templateDescription.trim(),
      tags: tags,
      jobTitle:
        typeof jobDescription.jobTitle === 'object'
          ? jobDescription.jobTitle?.id
          : jobDescription.jobTitle,
      workType: jobDescription.workType,
      minimumWorkExperience: jobDescription.minimumWorkExperience,
      jobLocation: jobDescription.jobLocation,
      responsibilities: jobDescription.responsibilities,
      educations: jobDescription.educations,
      certifications: jobDescription.certifications,
      mandatorySkills: jobDescription.mandatorySkills,
      desirableSkills: jobDescription.desirableSkills,
      behavioralAttributes: jobDescription.behavioralAttributes,
      aiScoreWeights: jobDescription.aiScoreWeights,
      documentRequirements: jobDescription.documentRequirements,
    };

    try {
      const response = await createJDTemplate(templateData);
      if (response.ok) {
        toast.success('Template Created!');
      } else {
        toast.error('Error saving templates', { position: 'bottom-center' });
      }
      onTemplateSave?.(templateData);
      closeModal();
    } catch (error) {
      toast.error('Error saving templates', { position: 'bottom-center' });
    } finally {
      setIsSaving(false);
    }
  };

  const inclusionsLeft = [
    'Job Title Configuration',
    'Work Type & Experience',
    'Location Settings',
  ];

  const inclusionsRight = [
    'Skills & Requirements',
    'AI Score Weightings',
    'Document Configuration',
    'All Lists & Attributes',
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', maxWidth: 1152, mx: 'auto' }}>
      <Stack spacing={3} sx={{ flex: 1 }}>
        {/* Template Name */}
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
            Template Name *
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder="e.g., Senior Frontend Developer Template"
            inputProps={{ maxLength: 100 }}
            helperText={`${templateName.length}/100 characters`}
          />
        </Box>

        {/* Template Description */}
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
            Description *
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={templateDescription}
            onChange={(e) => setTemplateDescription(e.target.value)}
            placeholder="Describe what this template is for and when to use it..."
            inputProps={{ maxLength: 300 }}
            helperText={`${templateDescription.length}/300 characters`}
          />
        </Box>

        {/* Tags */}
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
            Tags (Optional)
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={handleAddTag}
            placeholder="Type a tag and press Enter (e.g., React, JavaScript, Senior Level)"
            disabled={tags.length >= 8}
            helperText={`${tags.length}/8 tags added. Press Enter or comma to add tags.`}
          />
          {tags.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
              {tags.map((tag, index) => (
                <Chip
                  key={index}
                  label={tag}
                  size="small"
                  color="primary"
                  variant="outlined"
                  onDelete={() => removeTag(tag)}
                />
              ))}
            </Box>
          )}
        </Box>

        {/* Template Preview */}
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, bgcolor: 'grey.50' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'text.primary' }}>
            Template will include:
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <Stack spacing={1}>
              {inclusionsLeft.map((text, idx) => (
                <Stack key={idx} direction="row" spacing={1} alignItems="center">
                  <CheckCircle2 size={16} color="#16a34a" />
                  <Typography variant="body2" color="text.secondary">
                    {text}
                  </Typography>
                </Stack>
              ))}
            </Stack>
            <Stack spacing={1}>
              {inclusionsRight.map((text, idx) => (
                <Stack key={idx} direction="row" spacing={1} alignItems="center">
                  <CheckCircle2 size={16} color="#16a34a" />
                  <Typography variant="body2" color="text.secondary">
                    {text}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        </Paper>
      </Stack>

      {/* Action Buttons */}
      <Divider sx={{ my: 3 }} />
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Button
          variant="outlined"
          onClick={closeModal}
          disabled={isSaving}
          sx={{ textTransform: 'none' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!templateName.trim() || !templateDescription.trim() || isSaving}
          startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{ textTransform: 'none', px: 3 }}
        >
          {isSaving ? 'Saving Template...' : 'Save Template'}
        </Button>
      </Stack>
    </Box>
  );
};
